// A vessel's serial-numbers file: the yard's "SN - <SAP>.txt" in its Infocenter folder
// (infocenter/<year>/<SAP> - <NAME>/), as the desktop wrote it: the model and client, the SAP, then each
// console device and each engine with its part number and serial.
//   - buildSnText writes it in that layout; parseSnText reads one back (the yard's or ours) to edit it
//   - snFromOrder starts a new one from the boat's order (its Order Sheet row): Raymarine or Garmin, the
//     screens' size and how many, radar, thermal camera, VHF, AIS, and the engines (how many, which make and
//     model). With nothing on the order it is the yard's full standard list, as before.
// Pages: pages/SerialNumbers.tsx (one vessel), pages/Infocenter.tsx (Info / Files › Infocenter).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderValueOn } from '@technohull/shared';
import { api } from '../lib/api.js';

export type SnBrand = 'RAYMARINE' | 'GARMIN';
export const ENGINE_MAKERS = ['Mercury', 'Yamaha', 'Yanmar', 'Volvo'] as const;

export interface SnDevice { key: string; label: string; partNo: string; serial: string }
export interface SnEngine { key: string; hp: string; maker: string; partNo: string; serial: string }
export interface SnSheet { model: string; vesselName: string; sap: string; devices: SnDevice[]; engines: SnEngine[] }

let seq = 0;
export const snKey = () => `sn${++seq}`;
/** what an empty serial is written as, for the crew to fill in */
const NO_SERIAL = 'XXXXXXX';

type Kind = 'screen' | 'radar' | 'camera' | 'gps' | 'ais' | 'vhf';
// the yard's standard console fit, as it writes it (Raymarine with its part numbers; Garmin's differ per
// model and size, so they are left to fill in)
const KIT: Record<SnBrand, Array<[Kind, string, string]>> = {
  RAYMARINE: [
    ['screen', 'RAYMARINE AXIOM 2 PRO {{inches}} GPS', 'E706xx'],
    ['radar', 'QUANTUM DOPPLER RADAR', 'E70498'],
    ['camera', 'RAYMARINE THERMAL CAMERA', 'E70354'],
    ['gps', 'RAYMARINE RS150', 'E70310'],
    ['ais', 'RAYMARINE AIS700', 'E70476'],
    ['vhf', 'RAYMARINE RAY53 VHF', 'E70524'],
  ],
  GARMIN: [
    ['screen', 'GARMIN GPSMAP {{inches}}', ''],
    ['radar', 'GARMIN RADAR', ''],
    ['camera', 'GARMIN THERMAL CAMERA', ''],
    ['vhf', 'GARMIN VHF', ''],
    ['ais', 'GARMIN AIS', ''],
  ],
};

/** the console devices for a brand: `screens` of `inches` (two of unknown size when not said), and of the rest
 *  those `has` keeps (all of them when not said) */
export function snDevices(brand: SnBrand, o: { inches?: string; screens?: number; has?: (k: Kind) => boolean } = {}): SnDevice[] {
  const out: SnDevice[] = [];
  for (const [kind, label, partNo] of KIT[brand]) {
    if (kind === 'screen') {
      const text = o.inches ? label.replace('{{inches}}', `${o.inches}''`) : label;
      for (let i = 0; i < (o.screens ?? 2); i++) out.push({ key: snKey(), label: text, partNo, serial: '' });
    } else if (kind === 'gps' || !o.has || o.has(kind)) {
      out.push({ key: snKey(), label, partNo, serial: '' });
    }
  }
  return out;
}

export const newEngine = (maker = 'Mercury', hp = ''): SnEngine => ({ key: snKey(), hp, maker, partNo: '', serial: '' });

// the order template's rows (packages/shared domain/orderFile.ts) the S/N file starts from
const ORDER = {
  raymarine: 'AI', garmin: 'AJ', screens: [['AM', '16'], ['AN', '12'], ['AO', '9']] as const, second: 'AP', third: 'AQ',
  vhf: 'AR', ais: 'AT', camera: 'AW', radar: 'AX', count: 'S', makers: [['T', 'Mercury'], ['U', 'Yamaha'], ['V', 'Volvo'], ['W', 'Yanmar']] as const,
};

/** a new S/N file for a boat from its order (its Order Sheet row); `null` order → the yard's full list. `brand`
 *  overrides the order's (the page's Raymarine / Garmin switch). */
export function snFromOrder(o: { sap: string; vesselName: string; model: string; order?: Readonly<Record<string, string>> | null; brand?: SnBrand }): SnSheet & { brand: SnBrand; fromOrder: boolean } {
  const v = o.order ?? {};
  const on = (k: string) => orderValueOn(v[k]);
  const brand: SnBrand = o.brand ?? (on(ORDER.garmin) && !on(ORDER.raymarine) ? 'GARMIN' : 'RAYMARINE');
  const inches = ORDER.screens.find(([k]) => on(k))?.[1];
  const anyKit = [ORDER.raymarine, ORDER.garmin, ORDER.vhf, ORDER.ais, ORDER.camera, ORDER.radar, ...ORDER.screens.map(([k]) => k)].some(on);
  const devices = anyKit
    ? snDevices(brand, {
      inches,
      screens: inches ? 1 + (on(ORDER.second) ? 1 : 0) + (on(ORDER.third) ? 1 : 0) : 1,
      has: (k) => on(ORDER[k as 'vhf' | 'ais' | 'camera' | 'radar']),
    })
    : snDevices(brand);
  const maker = ORDER.makers.find(([k]) => on(k));
  const count = Math.min(Math.max(Number.parseInt(String(v[ORDER.count] ?? ''), 10) || 1, 1), 6);
  // the make's cell says which engine ("F150", "V8 300"), written as the yard writes its power
  const hp = maker ? String(v[maker[0]] ?? '').trim() : '';
  const engines = Array.from({ length: count }, () => newEngine(maker?.[1] ?? 'Mercury', /^(\+|x|yes|ναι)$/i.test(hp) ? '' : hp));
  return { sap: o.sap, vesselName: o.vesselName, model: o.model, devices, engines, brand, fromOrder: anyKit || Boolean(maker) };
}

/** the file, in the yard's layout */
export function buildSnText(o: SnSheet): string {
  const name = o.vesselName.trim();
  const head = `${o.model}${name ? ` - ${name}` : ''}\n${o.sap} \n___________________________________\n`;
  const blocks = [
    ...o.devices.map((d) => `${d.label}${d.partNo ? ` - ${d.partNo}` : ''}:\n${d.serial.trim() || NO_SERIAL}`),
    ...o.engines.map((e) => `${[e.hp, e.maker].filter(Boolean).join(' ')}${e.partNo ? ` - ${e.partNo}` : ''}:\n${e.serial.trim() || NO_SERIAL}`),
  ];
  return `${head}\n${blocks.join('\n\n')}\n`;
}

const MAKER_AT_END = new RegExp(`^(.*?)\\s*\\b(${ENGINE_MAKERS.join('|')})$`, 'i');

/** a saved file read back: its devices and engines (an engine is a line ending in its make), or null when it
 *  doesn't read as one */
export function parseSnText(text: string): SnSheet | null {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  // "<MODEL> - <NAME>", the name possibly blank ("GT7 - ", trimmed to "GT7 -")
  const head = /^(.*?)\s+-(?:\s+(.*))?$/.exec((lines[0] ?? '').trim());
  const sheet: SnSheet = {
    model: head ? head[1]!.trim() : (lines[0] ?? '').trim(),
    vesselName: head ? (head[2] ?? '').trim() : '',
    sap: (lines[1] ?? '').trim(),
    devices: [],
    engines: [],
  };
  // the entries: a "<what>[ - <part no.>]:" line, then its serial on the lines up to the next blank one
  let i = lines.findIndex((l) => /^_{5,}\s*$/.test(l.trim()));
  i = i < 0 ? 2 : i + 1;
  while (i < lines.length) {
    const line = (lines[i] ?? '').trim();
    i++;
    if (!line.endsWith(':')) continue;
    const what = line.slice(0, -1).trim();
    const serial: string[] = [];
    while (i < lines.length && (lines[i] ?? '').trim()) serial.push((lines[i++] ?? '').trim());
    const sn = serial.join(' ');
    const m = /^(.*?) - (\S+)$/.exec(what);
    const label = m ? m[1]!.trim() : what;
    const partNo = m ? m[2]! : '';
    const value = /^X{3,}$/i.test(sn) ? '' : sn;
    const eng = MAKER_AT_END.exec(label);
    if (eng) {
      const maker = ENGINE_MAKERS.find((x) => x.toLowerCase() === eng[2]!.toLowerCase()) ?? eng[2]!;
      sheet.engines.push({ key: snKey(), hp: eng[1]!.trim(), maker, partNo, serial: value });
    } else {
      sheet.devices.push({ key: snKey(), label, partNo, serial: value });
    }
  }
  return sheet.devices.length || sheet.engines.length ? sheet : null;
}

export const snFileName = (sap: string) => `SN - ${sap}.txt`;
// R2 keys are [A-Za-z0-9 _.()-] (apps/worker/src/routes/files.ts): a folder name kept inside them
const safeName = (s: string) => s.replace(/[^A-Za-z0-9 _.()-]+/g, ' ').replace(/\.{2,}/g, '.').replace(/\s+/g, ' ').trim();
/** a vessel's Infocenter folder name, "<SAP> - <NAME>" */
export const vesselFolderName = (sap: string, name: string) => safeName(`${sap} - ${name}`);
/** the folder a new vessel folder goes in: the season's ("infocenter/26-27/") */
export const infocenterYear = (season: string) => `infocenter/${safeName(season) || String(new Date().getFullYear())}/`;

export interface SnVessels {
  /** SAP → its S/N file (the newest when there are several) */
  index: Record<string, { key: string; name: string; uploaded: string }>;
  /** SAP → its vessel folder ("infocenter/<year>/<SAP> - <NAME>/") */
  folders: Record<string, string>;
}

/** every vessel's S/N file and folder in the Infocenter */
export function useSnVessels(enabled = true) {
  return useQuery({ queryKey: ['sn-vessels'], queryFn: () => api.get<SnVessels>('/api/files/sn-vessels'), enabled, staleTime: 60_000 });
}

/** a saved S/N file's text */
export function useSnText(key: string | undefined) {
  return useQuery({
    queryKey: ['sn-text', key],
    queryFn: async () => new TextDecoder().decode(await api.bytes(`/api/files/download?key=${encodeURIComponent(key!)}`)),
    enabled: Boolean(key),
    staleTime: 30_000,
  });
}

/** save a vessel's S/N file into its folder (the file's own, else the vessel's, else a new one in the season's year) */
export function useSaveSnFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { folder: string; sap: string; text: string }) => {
      const name = snFileName(v.sap);
      const up = await api.upload<{ ok: true; key: string }>(
        `/api/files/upload?prefix=${encodeURIComponent(v.folder)}&name=${encodeURIComponent(name)}`,
        new Blob([v.text], { type: 'text/plain;charset=utf-8' }),
        'text/plain;charset=utf-8',
      );
      return up.key;
    },
    onSuccess: (key) => {
      void qc.invalidateQueries({ queryKey: ['sn-vessels'] });
      void qc.invalidateQueries({ queryKey: ['sn-index'] });
      void qc.invalidateQueries({ queryKey: ['sn-text', key] });
      void qc.invalidateQueries({ queryKey: ['files'] });
    },
  });
}
